import React, { Component, ReactNode } from 'react';
import { Button } from 'antd';
import { BugOutlined } from '@ant-design/icons';
import { ChatError } from 'modules/chatbot/types/chatbot.types';
import { createChatError } from 'modules/chatbot/utils/validation';
import styles from './chatErrorBoundary.module.less';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: ChatError, errorInfo: React.ErrorInfo) => void;
  showErrorDetails?: boolean;
}

interface State {
  hasError: boolean;
  error: ChatError | null;
  errorInfo: React.ErrorInfo | null;
  retryCount: number;
}

class ChatErrorBoundary extends Component<Props, State> {
  private maxRetries = 3;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    const chatError = createChatError(
      'UNKNOWN_ERROR' as any,
      error.message || 'An unexpected error occurred',
      { originalError: error.name, stack: error.stack }
    );

    return {
      hasError: true,
      error: chatError,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('Chat Error Boundary caught an error:', error, errorInfo);
    this.logError(error, errorInfo);
  }

  private logError = (error: Error, errorInfo: React.ErrorInfo) => {
    console.error('ChatBot Error Boundary caught an error:', {
      error,
      errorInfo,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    });

    // In production, send to error monitoring service
    if (process.env.NODE_ENV === 'production') {
      // Example: Send to monitoring service
      // errorMonitoringService.captureError(error, errorInfo);
    }
  };

  handleRetry(): void {
    this.setState({ hasError: false, error: null });
  }

  handleReset() {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error } = this.state;

      return (
        <div className={styles.errorBoundary}>
          <div className={styles.errorContent}>
            <div className={styles.errorIcon}>
              <BugOutlined />
            </div>

            <h3 className={styles.errorTitle}>Oops! Something went wrong</h3>

            <p className={styles.errorMessage}>
              {error?.message ||
                'An unexpected error occurred in the chat system.'}
            </p>

            {this.props.showErrorDetails && error && (
              <details className={styles.errorDetails}>
                <summary>Error Details</summary>
                <pre className={styles.errorCode}>
                  {JSON.stringify(
                    {
                      code: error.code,
                      timestamp: new Date(error.timestamp).toLocaleString(),
                      context: error.context,
                    },
                    null,
                    2
                  )}
                </pre>
              </details>
            )}

            <div className={styles.errorActions}>
              <Button onClick={this.handleReset} className={styles.resetButton}>
                Reset Chat
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChatErrorBoundary;
